
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Compass, Loader2 } from "lucide-react";
import { TerraceFormValues } from "./types";

interface TerraceOrientationSelectProps {
  form: UseFormReturn<TerraceFormValues>;
  disabled?: boolean;
  showEstimateButton?: boolean;
  onEstimate?: () => void;
  isEstimating?: boolean;
}

const orientationOptions = [
  { value: "unknown", label: "Unknown" },
  { value: "N", label: "North (N)" },
  { value: "NE", label: "Northeast (NE)" },
  { value: "E", label: "East (E)" },
  { value: "SE", label: "Southeast (SE)" },
  { value: "S", label: "South (S)" },
  { value: "SW", label: "Southwest (SW)" },
  { value: "W", label: "West (W)" },
  { value: "NW", label: "Northwest (NW)" },
];

const TerraceOrientationSelect: React.FC<TerraceOrientationSelectProps> = ({ 
  form, 
  disabled = false,
  showEstimateButton = false,
  onEstimate,
  isEstimating = false
}) => {
  return (
    <FormField
      control={form.control}
      name="orientation"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Terrace Orientation</FormLabel>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Select 
                onValueChange={field.onChange} 
                value={field.value || "unknown"}
                disabled={disabled}
              >
                <FormControl>
                  <SelectTrigger className={disabled ? "bg-muted text-muted-foreground" : ""}>
                    <SelectValue placeholder="Select orientation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-border shadow-lg z-50 max-h-60 overflow-y-auto">
                  {orientationOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="bg-white hover:bg-gray-50 cursor-pointer"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {showEstimateButton && onEstimate && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onEstimate}
                disabled={isEstimating || disabled}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                {isEstimating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Compass className="h-4 w-4" />
                )}
                {isEstimating ? 'Estimating...' : 'Estimate'}
              </Button>
            )}
          </div>
          
          {disabled && (
            <FormDescription>
              This will be filled automatically from the Google Maps link
            </FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default TerraceOrientationSelect;
